import { useMemo, useState } from 'react'
import { fbt } from 'fbtee'
import { Search } from 'lucide-react'
import { hasMeSlot, openSlots, slotFor } from '@shared/eventTickets'
import { Button } from '../../../components/ui/button'
import { Section } from '../../../components/Section'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { useGuestGuard } from '../../../hooks/useGuestGuard'
import { giveOpenTicket, respondToTicket, setTicketSeeking, type EventTicketsView } from '../../../hooks/useEventTickets'
import { eventTicketsPrompt } from '../../../lib/guestPrompts'
import { isEventActive } from '../../../lib/formatDate'
import { getNow } from '../../../lib/clock'
import { TicketAssignmentCard } from './TicketAssignmentCard'
import { TicketLedgerCard } from './TicketLedgerCard'
import { TicketLedgerDialog } from './TicketLedgerDialog'
import { TicketSeekersList } from './TicketSeekersList'
import { TicketEntryRow } from './TicketEntryRow'
import { ticketsHaveContent } from './ticketsHaveContent'
import type { Event, EventTickets, Group, UserProfile } from '../../../types'

/**
 * A party's crew ticket coordination: the viewer's tickets and who each is for, tickets assigned
 * to them, and their crew's open tickets and seekers. Not shown on festivals (#661).
 */
export function TicketsSection({ event, tickets, currentUserId, groups, friendIds, profiles, profilesLoading, goingUserIds, interestedUserIds }: {
  event: Event
  tickets: EventTicketsView
  /** Signed-in, non-anonymous viewer; null for guests. */
  currentUserId: string | null
  groups: Group[]
  friendIds: string[]
  profiles: Record<string, UserProfile>
  profilesLoading: boolean
  /** The crew's RSVPs, so the holder picker can rank and mark people. */
  goingUserIds: ReadonlySet<string>
  interestedUserIds: ReadonlySet<string>
}) {
  const { prompt, isGuest } = useGuestGuard()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [givingBack, setGivingBack] = useState<{ ledger: EventTickets; slotId: string } | null>(null)
  const { mine, others, loading } = tickets
  const me = currentUserId ?? ''

  const memberGroups = useMemo(() => groups.filter(g => g.memberIds.includes(me)), [groups, me])
  const candidates = useMemo(
    () => [...new Set([...memberGroups.flatMap(g => g.memberIds), ...friendIds])],
    [memberGroups, friendIds],
  )
  const pendingForMe = others.filter(l => { const s = slotFor(l, me); return s && !s.accepted })
  const heldByMe = others.filter(l => slotFor(l, me)?.accepted)
  const crewOpen = others.filter(l => openSlots(l).length > 0 && !slotFor(l, me))
  const seekers = useMemo(() => others.filter(l => l.seeking).map(l => l.userId), [others])
  const seekerSet = useMemo(() => new Set(seekers), [seekers])
  const iHaveTicket = hasMeSlot(mine) || heldByMe.length > 0
  const mineHasTickets = (mine?.tickets ?? []).length > 0

  const canEdit = !event.cancelled && isEventActive(event, getNow())
  if (!canEdit && !loading && !mineHasTickets && heldByMe.length === 0) return null

  const guestPrompt = () => prompt(eventTicketsPrompt(event.id))
  async function run(action: () => Promise<unknown>) {
    if (busy) return
    setBusy(true)
    try { await action() } finally { setBusy(false) }
  }
  const respond = (ledger: EventTickets, slotId: string, action: Parameters<typeof respondToTicket>[2]) =>
    run(() => respondToTicket(ledger, slotId, action))

  const onAdd = mineHasTickets ? undefined : () => (currentUserId ? setDialogOpen(true) : guestPrompt())
  // Pointless while a ticket is already on offer to them: they can just say yes or claim it.
  const onSeek = mine?.seeking || iHaveTicket || pendingForMe.length > 0 || crewOpen.length > 0
    ? undefined
    : () => (currentUserId ? run(() => setTicketSeeking(event.id, true)) : guestPrompt())
  const hasContent = ticketsHaveContent(tickets, me, canEdit)
  // Until auth resolves there's no user and no guest prompt either: a tap would do nothing.
  const entryDisabled = busy || (!currentUserId && !isGuest)
  const compactEntry = canEdit && !loading && hasContent

  const giveTo = mine && openSlots(mine).length > 0 && canEdit
    ? (uid: string) => run(() => giveOpenTicket(mine, uid))
    : undefined

  return (
    <Section
      title={fbt('Tickets', 'Section heading on the event page for the tickets the viewer and their crew bought')}
      subtitle={fbt(
        'Who in your crew has a ticket, and who still needs one',
        'Subtitle under the Tickets heading on a party page: the section coordinates the crew’s tickets, it doesn’t sell them',
      )}
      action={compactEntry && <TicketEntryRow compact onAdd={onAdd} onSeek={onSeek} disabled={entryDisabled} />}
      testId="tickets-section"
    >
      {loading ? (
        <TicketLedgerCard loading />
      ) : (
        <div className="flex flex-col gap-3">
          {canEdit && pendingForMe.map(l => (
            <TicketAssignmentCard
              key={l.id}
              ownerId={l.userId}
              owner={profiles[l.userId]}
              busy={busy}
              onAccept={() => respond(l, slotFor(l, me)!.id, 'accept')}
              onDecline={() => respond(l, slotFor(l, me)!.id, 'decline')}
            />
          ))}

          {mineHasTickets && (
            <TicketLedgerCard ledger={mine!} view="owner" profiles={profiles} onEdit={canEdit ? () => setDialogOpen(true) : undefined} />
          )}

          {canEdit && mine?.seeking && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm" data-testid="ticket-seeking-card">
              <Search className="size-4 shrink-0 text-primary" />
              <span className="flex-1">
                {fbt("You're looking for a ticket", 'Card shown while the viewer has flagged they are looking for a ticket')}
              </span>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => setTicketSeeking(event.id, false))}>
                {fbt("I'm sorted", 'Button clearing the viewer’s "looking for a ticket" flag')}
              </Button>
            </div>
          )}

          {heldByMe.map(l => (
            <TicketLedgerCard
              key={l.id}
              ledger={l}
              view="holder"
              viewerId={me}
              ended={!canEdit}
              profiles={profiles}
              busy={busy}
              onGiveBack={canEdit ? slotId => setGivingBack({ ledger: l, slotId }) : undefined}
            />
          ))}

          {canEdit && crewOpen.map(l => (
            <TicketLedgerCard
              key={l.id}
              ledger={l}
              view="crew"
              profiles={profiles}
              busy={busy}
              onClaim={iHaveTicket ? undefined : slotId => respond(l, slotId, 'claim')}
            />
          ))}

          {canEdit && <TicketSeekersList seekerIds={seekers} profiles={profiles} profilesLoading={profilesLoading} busy={busy} onGive={giveTo} />}

          {canEdit && !compactEntry && <TicketEntryRow onAdd={onAdd} onSeek={onSeek} disabled={entryDisabled} />}
        </div>
      )}

      {currentUserId && (
        <TicketLedgerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          event={event}
          userId={currentUserId}
          mine={mine}
          candidates={candidates}
          groups={memberGroups}
          seekerIds={seekerSet}
          goingUserIds={goingUserIds}
          interestedUserIds={interestedUserIds}
        />
      )}
      <ConfirmDialog
        open={!!givingBack}
        onOpenChange={open => !open && setGivingBack(null)}
        title={fbt('Give the ticket back?', 'Title of the confirm before returning a ticket someone gave the viewer')}
        description={fbt('It becomes open again, and they get a notification.', 'Body of the confirm before returning a ticket someone gave the viewer')}
        confirmLabel={fbt('Give it back', 'Confirm button returning a ticket someone gave the viewer')}
        onConfirm={() => givingBack && respond(givingBack.ledger, givingBack.slotId, 'give_back')}
      />
    </Section>
  )
}
